import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, configureAuth, refreshSession } from "../api/client";
import { Button, Field, Notice } from "../components/ui";

const AuthContext = createContext(null);

export function InitialPasswordDecisionDialog({ onCompleted }) {
  const [form, setForm] = useState({ currentPassword: "", newPassword: "" });
  const [state, setState] = useState({ status: "idle", error: "" });
  const submit = async (event) => {
    event.preventDefault();
    setState({ status: "saving", error: "" });
    try {
      const user = await api("/auth/initial-password-decision", { method: "POST", body: JSON.stringify({ currentPassword: form.currentPassword, newPassword: form.newPassword }) });
      onCompleted(user);
    } catch (error) { setState({ status: "error", error: error.message }); }
  };
  return <div className="dialog-backdrop"><section className="dialog" role="dialog" aria-modal="true" aria-labelledby="initial-password-title"><header><h2 id="initial-password-title">Neues Passwort erstellen</h2></header><form className="domain-form" onSubmit={submit}>
    <Notice>Das administrativ erzeugte Passwort ist nur vorübergehend. Erstellen Sie ein neues, persönliches Passwort, bevor Sie fortfahren.</Notice>
    <Field id="initial-current-password" label="Vorübergehendes Passwort"><input required minLength="12" type="password" autoComplete="current-password" value={form.currentPassword} onChange={(event) => setForm({ ...form, currentPassword: event.target.value })}/></Field><Field id="initial-new-password" label="Neues Passwort"><input required minLength="12" type="password" autoComplete="new-password" value={form.newPassword} onChange={(event) => setForm({ ...form, newPassword: event.target.value })}/></Field>
    {state.error && <Notice tone="error">{state.error}</Notice>}<div className="form-actions"><Button disabled={state.status === "saving"}>{state.status === "saving" ? "Passwort wird gespeichert …" : "Neues Passwort speichern"}</Button></div>
  </form></section></div>;
}

export function AuthProvider({ children }) {
  const [session, setSessionState] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionRef = useRef(null);
  const setSession = (next) => {
    const normalized = next?.user ? { ...next, user: { ...next.user, mitarbeiterId: next.user.mitarbeiterId || null } } : next;
    sessionRef.current = normalized; setSessionState(normalized);
  };

  configureAuth({
    getAccessToken: () => sessionRef.current?.accessToken || null,
    onSession: setSession,
    onSignOut: () => setSession(null),
  });

  useEffect(() => {
    let active = true;
    refreshSession().catch(() => {}).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const login = async (credentials) => {
    const next = await api("/auth/login", { method: "POST", body: JSON.stringify(credentials) });
    setSession(next);
  };
  const logout = async () => {
    try { await api("/auth/logout", { method: "POST" }); }
    finally { setSession(null); }
  };
  const completeInitialPasswordDecision = (user) => setSession({ ...sessionRef.current, user });

  return <AuthContext.Provider value={{ session, user: session?.user || null, loading, login, logout }}>{children}{session?.user?.initialPasswordDecisionPending && <InitialPasswordDecisionDialog onCompleted={completeInitialPasswordDecision}/>}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
